import clsx from 'clsx';
import { Key, useCallback } from 'react';
import {
  DatabaseBroker,
  FrontendBroker,
  FrontendLoad,
} from '@trucksmarter/shared';
import {
  DallasList,
  DallasVirtualListItem,
  ListItemTemplateProps,
} from '@ui/Dallas/List/DallasList';
import {
  formatLoadPrice,
  formatLoadRpm,
  formatMiles,
  formatTrailerLength,
  formatTrailerTypes,
  formatWeight,
} from '@trucksmarter/shared_frontend';
import { toRelativeFromISO } from '~/utils/datetime';
import { useBrokers } from '@ui/shared/BrokersProvider/BrokersProvider';
import { EMPTY_VALUE_PLACEHOLDER } from '@ui/shared/text-placeholders';
import { LoadGridListItemSkeleton } from './LoadGridListSkeleton';
import { useViewedLoadIds } from '../LoadBoardViewedLoadIdsProvider';
import { useIncomingLoadIds } from '../LoadBoardIncomingLoadIdsByQueryStringProvider';
import DallasCard from '@ui/Dallas/Card/DallasCard';
import DallasStack from '@ui/Dallas/Stack/DallasStack';
import BrokerDisplayLogo from '../../../../_ui/BrokerDisplay/BrokerDisplayLogo';
import DallasText from '@ui/Dallas/Text/DallasText';
import DallasTag from '@ui/Dallas/Tag/DallasTag';
import LoadGridListStopsSummary from './LoadGridListStopsSummary';
import LoadUnavailableIndicatorType from '../load-unavailable-overlay-type';

import styles from './LoadGridList.module.scss';
import { useLoadsSearchQueryString } from '../LoadsSearchRequestProvider';

function GET_LOAD_KEY(load: FrontendLoad, _: number): Key {
  return load.id;
}

interface Props {
  className?: string;
  loadCount?: number;
  loads: FrontendLoad[];
  hasMoreLoads?: boolean;
  emptyStateContent: React.ReactElement;
  onLoadClick?: (load: FrontendLoad) => void;
  onLastLoadInView?: () => Promise<void> | void;
  loadUnavailableIndicatorType?: LoadUnavailableIndicatorType;
}

export default function LoadGridList({
  className,
  loads,
  hasMoreLoads = false,
  onLoadClick,
  onLastLoadInView,
  loadUnavailableIndicatorType = LoadUnavailableIndicatorType.None,
}: Props) {
  const brokers = useBrokers();
  const listItemTemplate = useCallback(
    ({ dataItem: load, last: isLast }: ListItemTemplateProps<FrontendLoad>) => {
      return (
        <LoadGridListItem
          load={load}
          onClick={onLoadClick ? () => onLoadClick(load) : undefined}
          last={isLast}
          broker={brokers[load.broker]}
          loadUnavailableIndicatorType={loadUnavailableIndicatorType}
        />
      );
    },
    [brokers, loadUnavailableIndicatorType, onLoadClick],
  );

  return (
    <DallasList
      className={clsx(className, styles.root)}
      data={loads}
      hasMoreItems={hasMoreLoads}
      getDataItemKey={GET_LOAD_KEY}
      listItemTemplate={listItemTemplate}
      onLastItemInView={onLastLoadInView}
      loadingIndicator={
        <>
          <LoadGridListItemSkeleton />
          <LoadGridListItemSkeleton />
          <LoadGridListItemSkeleton />
          <LoadGridListItemSkeleton />
        </>
      }
    />
  );
}

interface LoadGridListItemProps {
  load: FrontendLoad;
  broker?: FrontendBroker;
  onClick?: () => void;
  loadUnavailableIndicatorType?: LoadUnavailableIndicatorType;
  last: boolean;
}

function LoadGridListItem({
  load,
  broker,
  onClick,
  loadUnavailableIndicatorType,
  last: isLast,
}: LoadGridListItemProps) {
  const { ratePerMile, price, priceEstimate, distance, equipment, weight } =
    load;
  const displayPrice =
    formatLoadPrice(price, priceEstimate).replace('$ -', '') ||
    EMPTY_VALUE_PLACEHOLDER;
  const displayRatePerMile =
    formatLoadRpm(ratePerMile, priceEstimate).replace('$ -', '') ||
    EMPTY_VALUE_PLACEHOLDER;
  const isUnavailableWithOverlay =
    !load.available &&
    loadUnavailableIndicatorType === LoadUnavailableIndicatorType.Overlay;
  const queryString = useLoadsSearchQueryString();
  const viewedLoadIds = useViewedLoadIds();
  const incomingLoadIds = useIncomingLoadIds(queryString);
  const isNotViewed = viewedLoadIds?.has(load.id) === false;
  const isIncoming = incomingLoadIds?.has(load.id) === true;

  return (
    <DallasVirtualListItem
      className={styles.listItem}
      onClick={!isUnavailableWithOverlay ? onClick : undefined}
      last={isLast}
      aria-disabled={isUnavailableWithOverlay}
    >
      <DallasCard
        gap="sm"
        header={
          <>
            {load.available && isNotViewed && (
              <div
                className={clsx(
                  styles.badge,
                  isIncoming ? styles.incoming : styles.notViewed,
                )}
              />
            )}
            <header className={styles.header}>
              <DallasStack
                className={styles.priceRow}
                gap="sm"
                direction="row"
                alignVertical="center"
                flex
              >
                <DallasText variant="headerBoldS" noWrap>
                  {displayPrice}
                </DallasText>
                <DallasText color="darkGray" noWrap>
                  {displayRatePerMile}
                </DallasText>
              </DallasStack>
              <BrokerDisplayLogo
                className={styles.brokerLogo}
                broker={broker}
                textNoWrap
              />
            </header>
          </>
        }
        content={
          <>
            <DallasStack gap="xs" direction="row">
              {!!distance && <DallasTag text={formatMiles(distance)} />}
              {!!equipment.length && (
                <DallasTag
                  text={`${formatTrailerLength(
                    equipment.length,
                  )}' ${formatTrailerTypes(equipment.trailerTypes)}`}
                />
              )}
              {!!weight && (
                <DallasTag text={formatWeight(weight, { shorthand: true })} />
              )}
            </DallasStack>
            {isUnavailableWithOverlay && <LoadUnavailableCardOverlay />}
          </>
        }
        footer={
          <div className={styles.footer}>
            {!!broker?.broker && (
              <LoadGridListStopsSummary
                className={styles.stopsSummary}
                stops={load.stops}
                brokerId={broker?.broker as DatabaseBroker}
              />
            )}
            <DallasText
              className={styles.age}
              variant="captionL"
              color="darkGray"
              noWrap
            >
              {toRelativeFromISO(load.createdAt, { maxDays: 2 })}
            </DallasText>
          </div>
        }
        bordered
      />
    </DallasVirtualListItem>
  );
}

function LoadUnavailableCardOverlay() {
  return (
    <DallasStack
      className={styles.loadUnavailableOverlay}
      alignHorizontal="center"
      alignVertical="center"
    >
      <DallasTag text="Not available" />
    </DallasStack>
  );
}
